import { useState } from "react";
import { Form, Badge, NavLink, ProgressBar } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import CreatableSelect from "react-select/creatable";

import { storageDelete, tagManagerSearch, updatePersonAttachmentCount } from "../../api"; // storageMeta
import { Confirmation } from "../confirmation";
import { TrashIcon, PencilIcon, SimpleSaveIcon } from "../icons";
import { jsError, localized } from "../utils";
import { creatableSelectCodeSettings } from "../reactselect";
import { Button } from "../Button.tsx";
import styled from 'styled-components'

const StyledBadge = styled(Badge)`
  background-color: #5952e0 !important;
  color: #FCFCFD;
`;

export function AttachmentRow({ data, changeData, open, search, section, readOnly, isPersonal }) {
  const { t } = useTranslation();

  const sel = <option value="">{t("Select", "Valitse")}</option>;
  const opt = (translationKey, defaultValue) => (
    <option value={translationKey}>{t(translationKey, defaultValue)}</option>
  );

  const sectionMap = {
    person: (
      <>
        {sel}
        {opt("ProfilePhoto", "Profiilikuva")}
        {opt("Photo", "Kuva")}
        {opt("WorkContract", "Työsopimus")}
        {opt("Agreement", "Sopimus")}
        {opt("Other", "Muu")}
      </>
    ),
    contract: (
      <>
        {sel}
        {opt("WorkContract", "Työsopimus")}
        {opt("ConfidentialityAgreement", "Salassapitosopimus")}
        {opt("PartTimeContract", "Osa-aikasopimus")}
        {opt("Other", "Muu")}
      </>
    ),
    education: (
      <>
        {sel}
        {opt("Certificates", "Todistukset")}
        {opt("Courses", "Kurssit")}
        {opt("Apprenticeship", "Oppisopimus")}
        {opt("Assignment", "Toimeksianto")}
        {opt("TrainingContract", "Harjoittelusopimus")}
        {opt("Other", "Muu")}
      </>
    ),
    course: (
      <>
        {sel}
        {opt("Certificates", "Todistukset")}
        {opt("Courses", "Kurssit")}
        {opt("Program", "Ohjelma")}
        {opt("Other", "Muu")}
      </>
    ),
    skill: (
      <>
        {sel}
        {opt("Certificates", "Todistukset")}
        {opt("Courses", "Kurssit")}
        {opt("Evaluation", "Arviointi")}
        {opt("Other", "Muu")}
      </>
    ),
    languageSkill: (
      <>
        {sel}
        {opt("Certificates", "Todistukset")}
        {opt("Courses", "Kurssit")}
        {opt("Other", "Muu")}
      </>
    ),
    discussion: (
      <>
        {sel}
        {opt("Certificates", "Todistukset")}
        {opt("Courses", "Kurssit")}
        {opt("Discussion", "Keskustelu")}
        {opt("OccupationalHealth", "Työterveys")}
        {opt("Other", "Muu")}
      </>
    ),
    equipment: (
      <>
        {sel}
        {opt("Receipt", "Kuitti")}
        {opt("AgreementRental", "Vuokrasopimus")}
        {opt("Bicycle", "Polkupyörä")}
        {opt("Car", "Auto")}
        {opt("BenefitContract", "Sopimus edusta")}
        {opt("AgreementLeasing", "Leasingsopimus")}
        {opt("Other", "Muu")}
      </>
    ),
    document: (
      <>
        {sel}
        
          {opt("Certificates", "Todistukset")}
          {opt("Induction", "Perehdytys")}
          {opt("AgreementOccupationalHealth", "Työterveyssopimus")}
          {opt("AgreementInsurance", "Vakuutussopimus")}
          {opt("Instructions", "Ohjeet")}
          {opt("Other", "Muu")}
          <optgroup label={t("OwnProfile", "Oma profiili")}>
            {opt("PersonnelBenefits", "Henkilöstöedut")}
            {opt("CBA", t("CBA", "Työehtosopimus"))}
            {opt("OccupationalSafetyAndHealth", t("OccupationalSafetyAndHealth", "Työsuojelu"))}
            {opt("OccupationalHealth", t("OccupationalHealth", "Työterveys"))}
            {opt("TravelGuidelines", t("TravelGuidelines", "Matkustusohjeet"))}
            {opt("OccupationalSafety", t("OccupationalSafety", "Työturvallisuus"))}
            {opt("InformationSecurity", t("InformationSecurity", "Tietoturva"))}
            {opt("FireSafety", t("FireSafety", "Paloturvallisuus"))}
            {opt("HelpDesk", t("HelpDesk", "Help desk"))}
            {opt("DressCode", t("DressCode", "Pukuohjeistus"))}
            {opt("Ergonomy", t("Ergonomy", "Ergonomia"))}
            {opt("OtherInstruction", "Muu ohjeistus")}
          </optgroup>
      </>
    ),
  };

  const categoryOptions = sectionMap[section]; // attachment type

  const [tagList, setTagList] = useState([]);
  async function getTagList() {
    const tagList = await tagManagerSearch(); // parameters: keyPrefix, amount
    setTagList(
      tagList.data.map((tag) => ({
        value: tag.codeKey,
        label: tag.codeKey,
      })),
    );
  }

  const [editMode, setEditMode] = useState(() => {
    if (data.key === null) {
      getTagList();
      return true;
    } else return false;
  });

  const toggleEditMode = () => {
    if (!editMode) getTagList(); // TODO: useMemo in parent?
    setEditMode(!editMode);
  };

  /*
    // saving metadata is done in parent after saving entity
    async function save() {
      if (data.key) {
        const newData = {
          key: data.key,
          filename: data.filename,
          type: data.type,
          description: data.description,
          tags: data.tags,
          section: data.section,
          entityid: data.entityid,
        };
        storageMeta(newData);
      }

      toggleEditMode();
    }
  */

  async function remove() {
    if (data.key) {
      try {
        const promise = await storageDelete({
          key: data.key,
          section: data.section,
          entityid: data.entityid,
        });
        if (data.section === "person" && promise.data.count != null) {
          updatePersonAttachmentCount({
            personId: data.entityid,
            otherDetails: {
              attachmentCount: Number(promise.data.count),
            },
          });
        }
        search(); // update list
      } catch (error) {
        jsError(error.message);
      }
    } else {
      changeData({
        removeIdx: data.idx,
      });
    }
  }

  const handleTags = (tags) =>
    changeData({
      changeIdx: data.idx,
      payload: {
        tags: tags ? tags.map((item) => item.value) : [],
      },
    });

  const handleChange = ({ target }) =>
    changeData({
      changeIdx: data.idx,
      payload: {
        [target.id]: target.value,
      },
    });

  const openAttachment = () => open(data);

  const { resolvedLanguage } = localized;
  const createdDate = new Date(data.created);

  // upload progress
  if (data.progress !== undefined)
    return (
      <tr>
        <td>{data.filename}</td>
        <td colSpan={5}>
          <ProgressBar now={data.progress} label={`${data.progress}%`} animated={data.progress < 100} />
        </td>
      </tr>
    );
  else if (editMode)
    return (
      <tr>
        <td>
          <Form.Control
            disabled={isPersonal}
            value={data.filename ?? ""}
            id="filename"
            onChange={handleChange}
            placeholder={t("Filename", "Tiedoston nimi")}
          />
        </td>
        <td title={createdDate.toLocaleString(resolvedLanguage)}>{createdDate.toLocaleDateString(resolvedLanguage)}</td>
        <td>
          <Form.Select disabled={isPersonal} value={data.type ?? ""} id="type" onChange={handleChange}>
            {categoryOptions}
          </Form.Select>
        </td>
        <td>
          <Form.Control
            value={data.description ?? ""}
            id="description"
            onChange={handleChange}
            placeholder={t("Description", "Kuvaus")}
            disabled={isPersonal}
          />
        </td>
        <td>
          <CreatableSelect
            {...creatableSelectCodeSettings(t)}
            isMulti
            menuPosition="fixed"
            options={tagList}
            value={data.tags?.map((tag) => ({ value: tag, label: tag }))}
            onChange={handleTags}
            placeholder={t("Tags", "Tunnisteet")}
            disabled={isPersonal}
          />
        </td>
        <td>
          <Button testId="toggle-edit" variant="outline-primary" onClick={toggleEditMode} style={{ width: "40px", height: "40px" }}>
            <SimpleSaveIcon />
          </Button>
        </td>
      </tr>
    );
  else
    return (
      <tr>
        <td onClick={openAttachment}>
          <NavLink>{data.filename}</NavLink>
        </td>
        <td title={createdDate.toLocaleString(resolvedLanguage)}>{createdDate.toLocaleDateString(resolvedLanguage)}</td>
        <td>{t(data.type)}</td>
        <td>{data.description}</td>
        <td>
          {data.tags &&
            data.tags.map((tag, idx) => (
              <StyledBadge key={idx}>
                {tag}
              </StyledBadge>
            ))}
        </td>
        {!readOnly && !isPersonal && (
          <>
            <td>
              <div style={{ display: "flex", flexDirection: "row", gap: "8px", alignItems: "flex-start" }}>
                <Button testId="edit" variant="outline-warning" onClick={toggleEditMode} style={{ width: "40px", height: "40px" }}>
                  <PencilIcon />
                </Button>
                <Confirmation title={t("DeleteAttachment", "Poista liite")} actionFunction={remove}>
                  <Button
                    testId="delete"
                    key="key"
                    variant="outline-primary"
                    title={t("DeleteAttachment", "Poista liite")}
                    style={{ width: "40px", height: "40px" }}
                  >
                    <TrashIcon />
                  </Button>
                </Confirmation>
              </div>
            </td>
          </>
        )}
      </tr>
    );
}
